import { CardFace, ReviewedText, tip } from "./assets.tsx";
import { EquipmentCard, RoleCardView } from "./cards.tsx";
import { GLOSSARY, abilityName, abilityTip, roleAbilityTip, roleCopy } from "./glossary.ts";
import { KleinMemoryCard } from "./klein-memory.tsx";
import { abilityStateClass, abilityStateText } from "./format.ts";
import { campAccent, playerTheme, playerThemeStyle } from "./themes.ts";
import type { Private, State } from "./types.ts";
import { campLabel } from "./types.ts";
import "./player-inspector.css";

export function PlayerInspector({ state, priv, me, targetId, labelOf, onClose }: {
  state: State;
  priv: Private | null;
  me: string;
  targetId: string;
  labelOf: (id: string) => string;
  onClose: () => void;
}) {
  const player = state.players.find((entry) => entry.id === targetId);
  if (!player) return null;
  const place = state.board.places.find((entry) => entry.slot === player.slot);
  const isMe = player.id === me;
  const role = isMe && priv?.role_card ? priv.role_card : player.revealed ? player.role : null;
  const reviewedRole = role ? roleCopy(role.id) : undefined;
  const themeIndex = Math.max(0, state.players.findIndex((entry) => entry.id === player.id));

  return (
    <section className="inspector" style={playerThemeStyle(themeIndex)} aria-label={`${player.name}的角色详情`}>
      <header className="inspector-head">
        <div>
          <span className="inspector-piece" aria-hidden="true">{playerTheme(themeIndex).piece}</span>
          <h2>{isMe ? "御身" : player.name}</h2>
          {player.is_judge && <span className="badge" tabIndex={0} {...tip(GLOSSARY.judge)}>審判</span>}
          {player.attack_immune_until_move && (
            <span className="badge is-immune" title="不受攻击伤害，直到本人下次开始移动">攻击免疫</span>
          )}
        </div>
        <button type="button" className="inspector-close" onClick={onClose} aria-label="关闭角色卡">×</button>
      </header>

      <div className="inspector-role" aria-label={role ? "完整角色卡与说明" : `${player.name}的隐藏角色卡；身份未明`}>
        {isMe && priv?.role_card ? (
          <RoleCardView role={priv.role_card} hidden={false} owner="自分" />
        ) : role ? (
          <figure
            className={`role-card has-face camp-${role.camp}`}
            tabIndex={0}
            {...tip({
              title: reviewedRole?.name ?? role.name,
              kicker: `${campLabel(role.camp)}${reviewedRole ? ` · 体力 ${reviewedRole.hp}` : ""}`,
              summary: reviewedRole ? undefined : GLOSSARY.reveal,
              detail: reviewedRole ? undefined : roleAbilityTip(role.id),
              titleRuns: reviewedRole?.nameRuns,
              reviewLines: reviewedRole?.lines,
              image: `/cards/${role.id}.webp`,
              color: campAccent(role.camp),
            })}
          >
            <CardFace id={role.id} alt={`${reviewedRole?.name ?? role.name} 的完整角色卡`} />
            <figcaption>
              <span className="camp-tag">{campLabel(role.camp)}</span>
              <span className="role-name">{reviewedRole?.name ?? role.name}</span>
            </figcaption>
            {reviewedRole && <ReviewedText lines={reviewedRole.lines} className="role-reviewed-intro" />}
          </figure>
        ) : (
          <RoleCardView role={null} hidden owner={player.name} />
        )}
      </div>

      <dl className="inspector-facts">
        <div><dt>伤害</dt><dd>{player.damage}{player.hp === undefined ? "" : ` / ${player.hp}`}</dd></div>
        <div><dt>所在</dt><dd>{place?.name ?? "未落位"}</dd></div>
        <div><dt>主题</dt><dd>{playerTheme(themeIndex).name}</dd></div>
        {player.attack_immune_until_move && <div><dt>防护</dt><dd>攻击免疫至下次移动</dd></div>}
      </dl>

      {player.equipment.length > 0 && (
        <div className="inspector-equipment">
          <h3>装備</h3>
          <div className="inspector-card-row">
            {player.equipment.map((id, index) => (
              <EquipmentCard key={id} id={id} label={labelOf(id)} tilt={(index - 1) * 1.4} />
            ))}
          </div>
        </div>
      )}

      {isMe && priv?.abilities && priv.abilities.length > 0 && (
        <ul className="ability-list">
          {priv.abilities.map((ability) => {
            const stateText = abilityStateText(ability);
            return (
              <li
                className={abilityStateClass(ability)}
                key={ability.id}
                tabIndex={0}
                {...tip({ title: abilityName(ability.id), kicker: stateText, summary: abilityTip(ability.id) })}
              >
                <span>{abilityName(ability.id)}</span>
                <span className="ability-state">{stateText}</span>
              </li>
            );
          })}
        </ul>
      )}

      {isMe && priv?.klein_memory && priv.klein_memory.length > 0 && (
        <div className="klein-memory">
          <h3>命蓮寺記録</h3>
          <div className="inspector-card-row">
            {priv.klein_memory.map((entry, index) => (
              <KleinMemoryCard
                className="inspector-record-card"
                key={`${entry.seq}-${index}`}
                entry={entry}
                players={state.players}
                labelOf={labelOf}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
